/* eslint-disable no-console */

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleOverlayOptions {
    maxLines?: number;
    startOpen?: boolean;
    openOnError?: boolean;
    addTripleTapToToggle?: boolean;
    hintText?: string; // e.g. "(triple-tap to toggle)"
}

export interface ConsoleOverlayApi {
    show: () => void;
    hide: () => void;
    toggle: () => void;
    originalConsole: Partial<Record<ConsoleLevel, (...args: unknown[]) => void>>;
}

declare global {
    interface Window {
        __consoleOverlayInstalled__?: boolean;
        __consoleOverlay__?: ConsoleOverlayApi;
    }
}

export function installConsoleOverlay(
    options: ConsoleOverlayOptions = {}
): ConsoleOverlayApi | undefined {
    // Must be browser
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const {
        maxLines = 400,
        startOpen = false,
        openOnError = true,
        addTripleTapToToggle = true,
        hintText = '(triple-tap to toggle)',
    } = options;

    if (window.__consoleOverlayInstalled__) return window.__consoleOverlay__;
    window.__consoleOverlayInstalled__ = true;

    // ---------- UI ----------
    const overlay = document.createElement('div');
    overlay.style.cssText = `
    position: fixed;
    inset: 10px;
    z-index: 2147483647;
    background: rgba(0,0,0,0.88);
    color: #f2f2f2;
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.55);
    display: ${startOpen ? 'block' : 'none'};
    overflow: hidden;
  `;

    const header = document.createElement('div');
    header.style.cssText = `
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.12);
    font: 13px/1.2 -apple-system, system-ui, Segoe UI, Roboto, sans-serif;
  `;

    const title = document.createElement('div');
    title.textContent = 'Console';
    title.style.cssText = 'font-weight: 600;';

    const hint = document.createElement('div');
    hint.textContent = hintText;
    hint.style.cssText = 'opacity: 0.75; font-size: 12px;';

    const spacer = document.createElement('div');
    spacer.style.cssText = 'flex: 1;';

    const btnCopy = document.createElement('button');
    btnCopy.type = 'button';
    btnCopy.textContent = 'Copy';
    btnCopy.style.cssText = buttonCss();

    const btnClear = document.createElement('button');
    btnClear.type = 'button';
    btnClear.textContent = 'Clear';
    btnClear.style.cssText = buttonCss();

    const btnClose = document.createElement('button');
    btnClose.type = 'button';
    btnClose.textContent = 'Close';
    btnClose.style.cssText = buttonCss();

    header.append(title, hint, spacer, btnCopy, btnClear, btnClose);

    const body = document.createElement('div');
    body.style.cssText = `
    height: calc(100% - 44px);
    padding: 10px 12px 14px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    white-space: pre-wrap;
    word-break: break-word;
    font: 12px/1.35 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  `;

    overlay.append(header, body);
    document.documentElement.appendChild(overlay);

    const show = () => {
        overlay.style.display = 'block';
    };
    const hide = () => {
        overlay.style.display = 'none';
    };
    const toggle = () => {
        overlay.style.display === 'none' ? show() : hide();
    };

    btnClose.onclick = hide;
    btnClear.onclick = () => {
        body.textContent = '';
    };

    btnCopy.onclick = async () => {
        const text = body.textContent ?? '';
        try {
            // Clipboard API may be restricted inside some webviews unless user-initiated
            await navigator.clipboard.writeText(text);
            btnCopy.textContent = 'Copied';
            window.setTimeout(() => (btnCopy.textContent = 'Copy'), 900);
        } catch {
            // Fallback: select text for manual copy
            const range = document.createRange();
            range.selectNodeContents(body);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }
    };

    // ---------- Formatting ----------
    function safeReplacer(): (key: string, value: unknown) => unknown {
        const seen = new WeakSet<object>();
        return (_k, v) => {
            if (typeof v === 'object' && v !== null) {
                const obj = v as object;
                if (seen.has(obj)) return '[Circular]';
                seen.add(obj);
            }
            return v;
        };
    }

    function fmt(arg: unknown): string {
        if (arg instanceof Error) return arg.stack || String(arg);
        if (typeof arg === 'string') return arg;
        try {
            return JSON.stringify(arg, safeReplacer(), 2);
        } catch {
            return String(arg);
        }
    }

    function append(level: ConsoleLevel, args: unknown[]): void {
        const ts = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
        const line = `[${ts}] ${level.toUpperCase()}: ${args.map(fmt).join(' ')}`;

        const existing = body.textContent ?? '';
        const lines = existing.length ? existing.split('\n') : [];
        lines.push(line);
        if (lines.length > maxLines) lines.splice(0, lines.length - maxLines);
        body.textContent = lines.join('\n');

        body.scrollTop = body.scrollHeight;
    }

    function buttonCss(): string {
        return `
      background: rgba(255,255,255,0.08);
      color: #fff;
      border: 1px solid rgba(255,255,255,0.18);
      border-radius: 10px;
      padding: 7px 10px;
      font: 13px/1 -apple-system, system-ui, Segoe UI, Roboto, sans-serif;
    `;
    }

    // ---------- Patch console ----------
    const originalConsole: ConsoleOverlayApi['originalConsole'] = {
        log: console.log.bind(console),
        info: console.info?.bind(console),
        warn: console.warn?.bind(console),
        error: console.error.bind(console),
        debug: console.debug?.bind(console),
    };

    const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];

    levels.forEach((level) => {
        const original = originalConsole[level];
        (console as unknown as Record<string, (...a: unknown[]) => void>)[level] = (
            ...args: unknown[]
        ) => {
            try {
                original?.(...args);
            } catch {
                // Some webviews behave oddly if console isn't available; ignore
            }

            append(level, args);

            if (openOnError && level === 'error') show();
        };
    });

    // ---------- Catch runtime failures ----------
    window.addEventListener('error', (e: ErrorEvent) => {
        // Some WKWebView errors surface here even if console isn't visible
        console.error('Uncaught error:', e.message, e.filename, `${e.lineno}:${e.colno}`);
    });

    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
        console.error('Unhandled rejection:', e.reason);
    });

    // ---------- iOS-friendly toggle: triple tap ----------
    if (addTripleTapToToggle) {
        let taps = 0;
        let timer: number | undefined;

        window.addEventListener(
            'touchend',
            () => {
                taps += 1;
                if (timer) window.clearTimeout(timer);
                timer = window.setTimeout(() => (taps = 0), 350);

                if (taps >= 5) {
                    taps = 0;
                    toggle();
                }
            },
            { passive: true }
        );
    }

    const api: ConsoleOverlayApi = { show, hide, toggle, originalConsole };
    window.__consoleOverlay__ = api;
    return api;
}
